import { ChatMarkdown } from './ChatMarkdown';
import { useState, useRef, useEffect } from 'react';
import { Send, Bot, User, Loader2, Sparkles, PanelRightClose, Mic, Square, Volume2, VolumeX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { cn } from '@/lib/utils';
import { useToast } from '@/hooks/use-toast';
import { useVoiceInput } from '@/hooks/useVoiceInput';
import { useSpeech } from '@/hooks/useSpeech';
import { readAutoSpeak, writeAutoSpeak } from '@/lib/speechLogic';
import { appendTranscript, formatElapsed } from '@/lib/voiceInputLogic';
import { supabase } from '@/integrations/supabase/client';
import { buildProjectDataSnapshot } from '@/lib/reportSnapshot';
import { getTemplate, PERIOD_DAYS } from '@/lib/reportTemplates';
import {
  applyAnswer, contextPills, emptyReportFlow, followUpPills, menuPills, menuQuestion, nextReportStep,
  parseAssistantReply, reportStepMessage, reportStepPills, withDefaults,
  type Pill, type ReportFlowState,
} from '@/lib/chatFlows';
import { useLocalization } from '@/hooks/useLocalization';
import { localizeCitations } from '@/lib/i18n';

interface Message {
  role: 'user' | 'assistant';
  content: string;
}

interface AIChatProps {
  onClose: () => void;
  /** 'floating' = legacy popover card; 'dock' = full-height right-hand panel. */
  variant?: 'floating' | 'dock';
  /** Where the user is; drives which pills are offered. */
  section?: string;
  view?: string;
  viewLabel?: string;
  /** Hides pills that would open a view the user's role cannot access. */
  canOpen?: (view: string) => boolean;
  onNavigate?: (view: string) => void;
  /** Starts the scripted report flow (e.g. from the Reports page); a new nonce restarts it. */
  startFlow?: { templateId?: string; nonce: number };
}

type PillMode = { kind: 'home' } | { kind: 'custom'; pills: Pill[]; /** Survives tab changes (mid-flow). */ sticky?: boolean };

const CHAT_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-chat`;

// localStorage can throw (blocked site data, private windows); callers treat null as "no storage".
const safeStorage = (): Storage | null => {
  try { return window.localStorage; } catch { return null; }
};

const AIChat = ({ onClose, variant = 'floating', section, view, viewLabel, canOpen, onNavigate, startFlow }: AIChatProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [failed, setFailed] = useState(false);
  const [mode, setMode] = useState<PillMode>({ kind: 'home' });
  const [flow, setFlow] = useState<ReportFlowState | null>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const { toast } = useToast();
  const { tx, t, config } = useLocalization();
  const voice = useVoiceInput({
    onText: (text) => setInput((prev) => appendTranscript(prev, text)),
    onWarning: (message) => toast({ title: t('Check the transcription'), description: message }),
    onError: (message) => toast({ title: t('Voice input'), description: message, variant: 'destructive' }),
  });
  const speech = useSpeech({
    uiLanguage: config.language,
    onNoVoice: () => toast({
      title: t('Voice playback'),
      description: t("Your browser has no on-device voice for this language, so the reply can't be read aloud."),
    }),
    onCloudFallback: () => toast({
      title: t('Voice playback'),
      description: t('The cloud voice is unavailable right now, so replies are read with your device voice.'),
    }),
  });
  const [autoSpeak, setAutoSpeak] = useState(() => readAutoSpeak(typeof window === 'undefined' ? null : safeStorage()));
  // send() awaits a long reply, so it reads the toggle through a ref to honour a change made mid-stream.
  const autoSpeakRef = useRef(autoSpeak);
  const toggleAutoSpeak = () => {
    const next = !autoSpeak;
    autoSpeakRef.current = next;
    setAutoSpeak(next);
    writeAutoSpeak(safeStorage(), next);
    if (!next) speech.stop();
  };

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages]);

  const streamChat = async (userMessages: Message[]) => {
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) throw new Error('Not authenticated');

    const resp = await fetch(CHAT_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ messages: userMessages, context: { section, view } }),
    });

    if (!resp.ok) {
      const error = await resp.json();
      throw new Error(error.error || 'Failed to get response');
    }

    if (!resp.body) throw new Error('No response body');

    const reader = resp.body.getReader();
    const decoder = new TextDecoder();
    let textBuffer = '';
    let assistantContent = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      textBuffer += decoder.decode(value, { stream: true });

      let newlineIndex: number;
      while ((newlineIndex = textBuffer.indexOf('\n')) !== -1) {
        let line = textBuffer.slice(0, newlineIndex);
        textBuffer = textBuffer.slice(newlineIndex + 1);

        if (line.endsWith('\r')) line = line.slice(0, -1);
        if (line.startsWith(':') || line.trim() === '') continue;
        if (!line.startsWith('data: ')) continue;

        const jsonStr = line.slice(6).trim();
        if (jsonStr === '[DONE]') break;

        try {
          const parsed = JSON.parse(jsonStr);
          const content = parsed.choices?.[0]?.delta?.content;
          if (content) {
            assistantContent += content;
            setMessages((prev) => {
              const last = prev[prev.length - 1];
              if (last?.role === 'assistant') {
                return prev.map((m, i) =>
                  i === prev.length - 1 ? { ...m, content: assistantContent } : m
                );
              }
              return [...prev, { role: 'assistant', content: assistantContent }];
            });
          }
        } catch {
          textBuffer = line + '\n' + textBuffer;
          break;
        }
      }
    }
    return assistantContent;
  };

  const say = (content: string) => setMessages((prev) => [...prev, { role: 'assistant', content }]);
  const hear = (content: string) => setMessages((prev) => [...prev, { role: 'user', content }]);
  const ctx = { section, view, canOpen };
  // Localise at render so every downstream use (label echo, typed-label matching, the prompt sent for 'ask' pills)
  // is consistent; pill ids stay English, so flows and keys are unaffected.
  const localizePill = (p: Pill): Pill => ({
    ...p,
    label: t(p.label),
    action: p.action.type === 'ask' ? { ...p.action, prompt: t(p.action.prompt) } : p.action,
  });
  const shownPills = (mode.kind === 'home' ? contextPills(ctx) : mode.pills).map(localizePill);

  // Scripted report flow: no model call, each step is a pill choice.
  const advance = (state: ReportFlowState, hint = '') => {
    const next = withDefaults(state);
    const step = nextReportStep(next, hint);
    setFlow(next);
    say(reportStepMessage(step, next));
    setMode({ kind: 'custom', pills: reportStepPills(step), sticky: true });
  };
  const advanceRef = useRef(advance);
  advanceRef.current = advance;

  useEffect(() => {
    if (startFlow) advanceRef.current(emptyReportFlow(startFlow.templateId));
  }, [startFlow]);

  // New tab: show that tab's pills, unless the user is mid-flow.
  useEffect(() => {
    setMode((prev) => (prev.kind === 'custom' && prev.sticky ? prev : { kind: 'home' }));
  }, [section, view]);

  const finish = (extra?: Pill[]) => setMode({ kind: 'custom', pills: extra ?? followUpPills(ctx, viewLabel) });

  const runReport = async (state: ReportFlowState) => {
    const template = getTemplate(state.templateId);
    if (!template) return;
    setIsLoading(true);
    try {
      const projectData = await buildProjectDataSnapshot(PERIOD_DAYS[state.answers.period] ?? 7);
      const { data, error } = await supabase.functions.invoke('generate-report', {
        body: { reportType: template.id, projectData, options: state.answers },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      window.dispatchEvent(new CustomEvent('masira:report-generated'));
      setFlow(null);
      say(`Your ${template.name} is ready and saved under Reports.`);
      finish([
        { id: 'go:reports', label: 'Open report', action: { type: 'navigate', view: 'reports' } },
        { id: 'flow:report', label: 'Create another report', action: { type: 'flow-start' } },
        ...followUpPills(ctx, viewLabel).slice(0, 1),
      ]);
    } catch (error) {
      toast({
        title: t('Report could not be created'),
        description: error instanceof Error ? error.message : t('Failed to generate report'),
        variant: 'destructive',
      });
      say('That did not work. Want me to try again?');
      setMode({ kind: 'custom', pills: [
        { id: 'flow:run', label: 'Try again', action: { type: 'flow-run' } },
        { id: 'flow:cancel', label: 'Cancel', action: { type: 'flow-cancel' } },
      ], sticky: true });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePill = (pill: Pill) => {
    const a = pill.action;
    switch (a.type) {
      case 'ask':
        void send(a.prompt, pill.label);
        return;
      case 'navigate':
        onNavigate?.(a.view);
        finish();
        return;
      case 'menu':
        hear(pill.label);
        say(t(menuQuestion(a.menuId)));
        setMode({ kind: 'custom', pills: menuPills(a.menuId) });
        return;
      case 'home':
        hear(pill.label);
        say('Sure. What would you like to do?');
        setFlow(null);
        setMode({ kind: 'home' });
        return;
      case 'flow-start':
        hear(pill.label);
        advance(emptyReportFlow(a.templateId));
        return;
      case 'flow-template':
        hear(pill.label);
        advance(emptyReportFlow(a.templateId));
        return;
      case 'flow-answer':
        if (!flow) return;
        hear(a.label);
        advance(applyAnswer(flow, a.key, a.value, a.label));
        return;
      case 'flow-run':
        if (flow) void runReport(flow);
        return;
      case 'flow-cancel':
        hear(pill.label);
        setFlow(null);
        say('No problem. What would you like to do instead?');
        setMode({ kind: 'home' });
        return;
    }
  };

  const send = async (text: string, label?: string) => {
    const content = text.trim();
    if (!content || isLoading) return;
    // Typing the label of a visible pill counts as clicking it (keeps flows on rails).
    const typed = shownPills.find((p) => p.label.toLowerCase() === content.toLowerCase());
    if (typed && label === undefined) {
      setInput('');
      handlePill(typed);
      return;
    }
    setFlow(null);
    speech.stop();
    const userMessage: Message = { role: 'user', content: label ?? content };
    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setFailed(false);
    setIsLoading(true);

    try {
      const raw = await streamChat([...messages, { role: 'user', content }]);
      const reply = parseAssistantReply(raw);
      // The assistant message lands after the user message appended above, i.e. at messages.length + 1.
      if (autoSpeakRef.current && reply.text) speech.speak(messages.length + 1, reply.text);
      if (reply.flow === 'report') {
        advance(emptyReportFlow(), content);
      } else if (reply.options.length) {
        setMode({ kind: 'custom', pills: [
          ...reply.options.map((o): Pill => ({ id: `ask:${o}`, label: o, action: { type: 'ask', prompt: o } })),
          ...followUpPills(ctx, viewLabel).slice(0, 1),
        ] });
      } else {
        finish();
      }
    } catch (error) {
      console.error('Chat error:', error);
      setFailed(true);
      toast({
        title: t('Masira AI could not answer'),
        description: error instanceof Error ? error.message : t('Failed to get response'),
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void send(input);
  };

  const isDock = variant === 'dock';

  return (
    <div
      className={cn(
        'flex flex-col h-full bg-card overflow-hidden',
        isDock ? 'border-s border-border' : 'border border-border rounded-xl shadow-2xl',
      )}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 py-3 border-b border-border bg-secondary/30">
        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
          <Sparkles className="w-4 h-4 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-heading font-semibold text-sm text-foreground">{isDock ? t('Ask Masira') : t('Masira AI')}</h3>
          <p className={cn('text-xs text-muted-foreground', isDock && 'font-mono')}>
            {isDock ? t('private workspace assistant') : t('Your project management assistant')}
          </p>
        </div>
        {speech.available && (
          <Button
            variant="ghost"
            size="icon"
            onClick={toggleAutoSpeak}
            aria-pressed={autoSpeak}
            aria-label={t('Read replies aloud')}
            className="text-muted-foreground"
          >
            {autoSpeak ? <Volume2 className="w-4 h-4" /> : <VolumeX className="w-4 h-4" />}
          </Button>
        )}
        {isDock && (
          <Button variant="ghost" size="icon" onClick={onClose} aria-label={t('Collapse Ask Masira panel')} className="text-muted-foreground">
            <PanelRightClose className="w-4 h-4" />
          </Button>
        )}
      </div>

      {/* Messages */}
      <ScrollArea className="flex-1 p-4" ref={scrollRef}>
        {messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-center text-muted-foreground">
            <Bot className="w-12 h-12 mb-3 opacity-50" />
            <p className="text-sm">{t("Hi! I'm Masira AI. How can I help you with your projects today?")}</p>
            <p className="mt-2 text-xs max-w-[16rem]">{t('Answers use only your connected workspace data.')}</p>
          </div>
        ) : (
          <div className="space-y-4">
            {messages.map((msg, i) => (
              <div
                key={i}
                className={cn(
                  'flex gap-3',
                  msg.role === 'user' && 'flex-row-reverse'
                )}
              >
                <div
                  className={cn(
                    'w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0',
                    msg.role === 'user' ? 'bg-primary' : 'bg-secondary'
                  )}
                >
                  {msg.role === 'user' ? (
                    <User className="w-4 h-4 text-primary-foreground" />
                  ) : (
                    <Bot className="w-4 h-4 text-foreground" />
                  )}
                </div>
                <div
                  className={cn(
                    'max-w-[80%] rounded-lg px-3 py-2 text-sm',
                    msg.role === 'user'
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-secondary text-foreground'
                  )}
                >
                  {msg.role === 'assistant'
                    ? (
                      <>
                        <ChatMarkdown text={localizeCitations(config.language, parseAssistantReply(msg.content).text)} />
                        {speech.available && !(isLoading && i === messages.length - 1) && (
                          <button
                            type="button"
                            onClick={() => (speech.speakingIndex === i ? speech.stop() : speech.speak(i, parseAssistantReply(msg.content).text))}
                            aria-label={speech.speakingIndex === i ? t('Stop reading aloud') : t('Read this reply aloud')}
                            className="mt-1 p-1 -m-1 rounded text-muted-foreground hover:text-foreground transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                          >
                            {speech.speakingIndex === i ? <Square className="w-3.5 h-3.5" /> : <Volume2 className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </>
                    )
                    : <p dir="auto" className="whitespace-pre-wrap">{msg.content}</p>}
                </div>
              </div>
            ))}
            {failed && !isLoading && (
              <p role="alert" className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-foreground">
                
                {t('Masira AI could not answer. If this keeps happening, ask an administrator to check that an AI provider is configured under Admin → AI Controls.')}
              </p>
            )}
            {isLoading && messages[messages.length - 1]?.role === 'user' && (
              <div className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center">
                  <Bot className="w-4 h-4 text-foreground" />
                </div>
                <div className="bg-secondary rounded-lg px-3 py-2">
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                </div>
              </div>
            )}
          </div>
        )}
      </ScrollArea>

      {shownPills.length > 0 && (
        <div className="flex flex-wrap gap-2 px-3 pb-2" role="group" aria-label={t('Suggested actions')}>
          {shownPills.map((p) => (
            <button
              key={p.id}
              type="button"
              disabled={isLoading}
              onClick={() => handlePill(p)}
              className="rounded-full border border-border bg-secondary px-3 py-1 text-xs text-secondary-foreground hover:bg-accent disabled:opacity-50 transition-colors"
            >
              {tx(p.label)}
            </button>
          ))}
        </div>
      )}

      {/* Input */}
      <form onSubmit={handleSubmit} className="p-3 border-t border-border bg-secondary/20">
        <div className="flex gap-2">
          <Input
            value={input}
            onChange={(e) => setInput(e.target.value)}
            placeholder={isDock ? t('Ask about your projects…') : t('Type a message...')}
            aria-label={t('Message Masira')}
            disabled={isLoading}
            className="flex-1"
          />
          {voice.available && (
            <Button
              type="button"
              size="icon"
              variant={voice.state === 'recording' ? 'destructive' : 'outline'}
              aria-label={voice.state === 'recording' ? t('Stop recording') : voice.state === 'transcribing' ? t('Transcribing') : t('Speak your question')}
              aria-busy={voice.state === 'transcribing' || voice.busy}
              disabled={voice.state === 'transcribing' || voice.busy || (isLoading && voice.state !== 'recording')}
              onClick={() => { if (voice.state !== 'recording') speech.stop(); voice.toggle(); }}
              className={voice.state === 'recording' ? 'animate-pulse' : undefined}
            >
              {voice.state === 'transcribing'
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : voice.state === 'recording' ? <Square className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
            </Button>
          )}
          <Button type="submit" size="icon" aria-label={t('Send message')} disabled={isLoading || !input.trim()}>
            <Send className="w-4 h-4" />
          </Button>
        </div>
        {voice.state === 'recording' && (
          <p role="status" className="mt-1 text-xs text-destructive">{t('Recording')} {formatElapsed(voice.elapsedMs)}</p>
        )}
      </form>
    </div>
  );
};

export default AIChat;
